import { useState, useEffect, useCallback, useMemo } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import ErrorScreen from "../../../../components/error/ErrorScreen";
import BarNivo from "../../train/graph/BarNivo";
import HeatMapGraph from "../../train/graph/HeatMapGraph";
import PieNivo from "../../train/graph/PieNivo";
import RadialChart from "../../train/graph/RadialChart";
import ScatterChart from "../../train/graph/ScatterChart";
import LineNivo from "../../train/graph/LineNivo";
import { LineChart } from "../../train/graph/LineChart";
import getDataFromApi from "../../../../components/getDataFromApi";
import Prediction from "../../../../components/modal/NavPanes/Prediction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faClose } from "@fortawesome/free-solid-svg-icons";
import { StyledTooltip } from "../../label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";

export default function VersionNav({ data, id, taskType, preview, datasetId, disableFetchMore, scatterFilterPlacement }) {
  const [plotData, setPlotData] = useState(data.plots_data || {});
  const [pageNumber, setPageNumber] = useState(Number(data?.page) || 1);
  const [totalPages, setTotalPages] = useState(
    Math.max(1, Number(data?.pages) || 1)
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [paginationError, setPaginationError] = useState("");
  const [pageInput, setPageInput] = useState(String(Number(data?.page) || 1));
  const [showCanvas, setShowCanvas] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);

  useEffect(() => {
    setPlotData(data?.plots_data || {});
    setPageNumber(Number(data?.page) || 1);
    setTotalPages(Math.max(1, Number(data?.pages) || 1));
    setPaginationError("");
  }, [data]);

  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  const goToPage = useCallback(async (requestedPage) => {
    const nextPage = Math.min(totalPages, Math.max(1, Number(requestedPage)));
    if (
      disableFetchMore ||
      loadingMore ||
      !Number.isFinite(nextPage) ||
      nextPage === pageNumber
    ) {
      return;
    }

    setLoadingMore(true);
    setPaginationError("");
    const url = `${window["ENV"].API_URL}/dataset_versions/${id}/plots?page_number=${nextPage}`;

    try {
      const newData = await getDataFromApi(url, "get");

      setPlotData(newData?.plots_data || {});
      setPageNumber(Number(newData?.page) || nextPage);
      setTotalPages(Math.max(1, Number(newData?.pages) || totalPages));
    } catch (error) {
      console.error("Error fetching analytics page:", error);
      setPaginationError(error?.message || "Unable to load this analytics page.");
    } finally {
      setLoadingMore(false);
    }
  }, [disableFetchMore, id, loadingMore, pageNumber, totalPages]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 12) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, 2, 3, 4, totalPages - 1, totalPages]);
    for (let page = pageNumber - 2; page <= pageNumber + 2; page += 1) {
      if (page >= 1 && page <= totalPages) pages.add(page);
    }

    const sortedPages = [...pages].sort((left, right) => left - right);
    return sortedPages.flatMap((page, index) =>
      index > 0 && page - sortedPages[index - 1] > 1 ? ["...", page] : [page]
    );
  }, [pageNumber, totalPages]);

  const submitPageInput = useCallback(() => {
    const requestedPage = Number(pageInput);
    if (!Number.isFinite(requestedPage) || requestedPage < 1 || requestedPage > totalPages) {
      return;
    }

    goToPage(requestedPage);
  }, [goToPage, pageInput, totalPages]);

  //console.table(plotData);

  const renderPlotComponent = () => {
    const plotKeys = Object.keys(plotData || {});
  
    // ✅ No plots -> show a CanvaImage-style empty state
    if (plotKeys.length === 0) {
      return (
        <div className="d-flex justify-content-center w-100 mt-4">
          <div className="p-4 text-center">
            <h6 className="fw-semibold">No analytics available</h6>
            <p className="text-secondary mb-0">
              This dataset version does not have any plots yet.
            </p>
            <span className="text-secondary">
              Analytics will appear here once there is enough data or completed experiments.
            </span>
          </div>
        </div>
      );
    }
  
    // ✅ Normal case: render plots
    return plotKeys.map((val) => {
      const plot = plotData[val];
  
      return (
        <div
          key={val}
          className="mb-1 mt-3 container-card btn-full-width"
          style={{
            scrollbarWidth: "none",
            marginRight: val.match("Annotated vs not annotated distribution")
              ? "1.5%"
              : val.match("Image Tag distribution")
              ? "0px"
              : "0px",
            width:
              val.match("Annotated vs not annotated distribution") ||
              val.match("Image Tag distribution")
                ? "49.1%"
                : "100%",
          }}
        >
          {plot.type.match("bar_nivo") && (
            <BarNivo
              data={plot.data}
              title={val}
              class_data={data?.idx2class ?? null}
              xlabel={plot.xlabel ?? plot.x_label}
              ylabel={plot.ylabel ?? plot.y_label}
              showLegend
            />
          )}
  
          {plot.type.match("pie") && (
            <div className="dataset-analytics-chart-frame">
              <PieNivo data={plot.data} title={val} showLegend />
            </div>
          )}
  
          {plot.type.match("heatmap") && (
            <HeatMapGraph data={plot.data} title={val} />
          )}
  
          {plot.type.match("radial_nivo") && (
            <RadialChart
              data={plot.data}
              title={val}
              showLegend
              align={
                val.match("Annotated vs not annotated distribution") ||
                val.match("Image Tag distribution")
                  ? true
                  : undefined
              }
            />
          )}
  
          {plot.type.match("line_nivo") && (
            <>
              <h6 className="text-center p-3">{val}</h6>
              <LineChart
                data={plot.data}
                title={val}
                xlabel={plot.xlabel}
                ylabel={plot.ylabel}
              />
            </>
          )}
  
          {plot.type.match("scatter_nivo") && (
            <ScatterChart
              data={plot.data}
              title={val}
              xlabel={plot.xlabel}
              ylabel={plot.ylabel}
              cssManage={true}
              filterPlacement={scatterFilterPlacement}
              showLegend
            />
          )}
        </div>
      );
    });
  };
  ;

  const navPanes = [
    {
      name: "Data",
      component: (
        <Prediction
          id={id}
          type={!preview ? "dataset_versions" : "datasets"}
          query={null}
          filter={"dataset_version"}
          preview={preview}
          datasetName={data?.info.dataset_name}
          datasetVersion={data?.info.version}
          datasetId={datasetId}
        />
      ),
    },
    {
      name: "Analytics",
      component: renderPlotComponent(),
    },
  ];

  const [status, setStatus] = useState(navPanes[1]);

  useEffect(() => {
    if (status.name === "Analytics") {
      setStatus((prevStatus) => ({
        ...prevStatus,
        component: renderPlotComponent(),
      }));
    }
  }, [plotData, status.name]);

  const [Data, setData] = useState(false);

  return (
    <>
      <div className="w-100 dataset-analytics-shell">
        <TabContent activeTab="1">
          <TabPane
            tabId="1"
            className="overflowY mt-2 px-0 plot-container dataset-analytics-plots"
            style={{
              // maxHeight: "calc(100vh - 130px)",
              // overflowY: "auto",
              // position: "sticky",
              // overflowX: "clip",
              // scrollbarWidth: "thin",
              display:'flex',
              flexWrap:'wrap'
            }}
          >
            {status.component}
            {loadingMore && (
              <div className="dataset-analytics-page-loader">
                Loading analytics page...
              </div>
            )}
          </TabPane>
        </TabContent>

        {!disableFetchMore && totalPages > 1 && (
          <nav
            className="dataset-analytics-pagination"
            aria-label="Dataset analytics pagination"
          >
            <div className="dataset-analytics-pagination__controls">
              <div className="dataset-analytics-pagination__pages">
                {paginationItems.map((item, index) =>
                  item === "..." ? (
                    <span
                      key={`analytics-page-gap-${index}`}
                      className="dataset-analytics-pagination__ellipsis"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={`analytics-page-${item}`}
                      className={`dataset-analytics-pagination__page ${
                        item === pageNumber ? "is-active" : ""
                      }`}
                      onClick={() => goToPage(item)}
                      disabled={loadingMore || item === pageNumber}
                      aria-current={item === pageNumber ? "page" : undefined}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>

              <div className="dataset-analytics-pagination__go">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Dataset analytics page number"
                  className="dataset-analytics-pagination__input"
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value.replace(/[^\d]/g, ""))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitPageInput();
                    }
                  }}
                  disabled={loadingMore}
                />
                <button
                  type="button"
                  className="dataset-analytics-pagination__go-button"
                  onClick={submitPageInput}
                  disabled={loadingMore}
                >
                  GO
                </button>
              </div>
            </div>
          </nav>
        )}

        {paginationError && (
          <div className="dataset-analytics-pagination__error" role="alert">
            {paginationError}
          </div>
        )}
      </div>

      {Data && (
        <div className="sample-bg">
          <div
            className="modal-902 ReactModal__Content"
            style={{
              position: "absolute",
              width: "80%",
              background: "red",
              top: "1",
              left: "0",
            }}
            onClick={(e) => e.stopPropagation()}
          >

            <Prediction
              id={id}
              type={!preview ? "dataset_versions" : "datasets"}
              filter={"dataset_version"}
              preview={preview}
              datasetName={data?.info.dataset_name}
              datasetVersion={data?.info.version}
              sliderIndex={sliderIndex}
              setSliderIndex={setSliderIndex}
              datasetId={datasetId}
              visibleCanvas={false}
              setPredictions={setData}
            />
          </div>
        </div>
      )}
    </>
  );
}
